// CT600 Corporation Tax filing wizard types

export type CT600WizardStepId = 1 | 2 | 3 | 4 | 5 | 6 | 7

export interface CT600WizardStep {
  number: CT600WizardStepId
  title: string
}

// Step 1: Setup
export interface CT600SetupForm {
  companyId: string
  companyNumber: string
  companyName: string
  accountingPeriodStart: string
  accountingPeriodEnd: string
  utr: string
  returnType: 'full' | 'tax-only' | 'accounts-only'
  planSlug: string
}

/**
 * Backend enum for the return scope. Map from the user-facing `returnType`
 * dropdown values via `toFilingScope`.
 */
export type CT600FilingScope = 'ct_and_accounts' | 'ct_only' | 'accounts_only'

export function toFilingScope(
  returnType: CT600SetupForm['returnType']
): CT600FilingScope {
  switch (returnType) {
    case 'tax-only':
      return 'ct_only'
    case 'accounts-only':
      return 'accounts_only'
    case 'full':
    default:
      return 'ct_and_accounts'
  }
}

/**
 * Reverse of `toFilingScope` — maps the backend filing scope back to the
 * user-facing setup dropdown value when an existing form is loaded.
 */
export function fromFilingScope(
  scope: string | undefined | null
): CT600SetupForm['returnType'] {
  switch (scope) {
    case 'ct_only':
      return 'tax-only'
    case 'accounts_only':
      return 'accounts-only'
    case 'ct_and_accounts':
    default:
      return 'full'
  }
}

/**
 * The backend `returnType` enum is the CT600 return *variant* (which form is
 * filed), NOT the filing scope. It's derived from the company flags rather
 * than picked directly, since the "Return Type" dropdown now selects scope.
 */
export type CT600ReturnType = 'full' | 'short' | 'amended' | 'dormant'

export function toReturnType(opts: {
  isDormant?: boolean
  amendedReturn?: boolean
}): CT600ReturnType {
  if (opts.isDormant) return 'dormant'
  if (opts.amendedReturn) return 'amended'
  return 'full'
}

// Step 2: Company Information (maps to PATCH companyInformation)
export interface CT600CompanyInfoForm {
  details: {
    returnType: string
    firstReturn: boolean
    finalReturn: boolean
    amendedReturn: boolean
    companyName: string
    companyRegistrationNumber: string
    utr: string
    tradingName: string
    accountingPeriod: { start: string; end: string }
  }
  flags: {
    isDormant: boolean
    isCloseCompany: boolean
    isInvestmentCompany: boolean
  }
  contact: {
    name: string
    phone: string
    email: string
  }
  registeredOffice: {
    addressLine1: string
    addressLine2: string
    town: string
    county: string
    postCode: string
    country: string
  }
  // Maps to PATCH companyInformation.ixbrlFiling. Stored as a string in
  // form state so the input can be controlled; parsed to a number on save.
  ixbrlFiling: {
    averageEmployees: string
  }
}

// Step 3: P&L (maps to PATCH financialData)
export interface CT600FinancialDataForm {
  turnover: string
  otherIncome: string
  costOfSales: string
  distributionCosts: string
  administrativeExpenses: string
  interestReceivable: string
  interestPayable: string
  /** Tax expense (corporation tax only) — used to compute Profit After Tax. */
  taxExpense: string
}

// Step 3: Balance Sheet (maps to PATCH balanceSheet)
export interface CT600BalanceSheetForm {
  intangibleAssets: string
  tangibleAssets: string
  stocks: string
  debtors: string
  cashAtBankAndInHand: string
  creditorsDueWithinOneYear: string
  creditorsDueAfterOneYear: string
  calledUpShareCapital: string
  /**
   * Opening (brought-forward) Profit & Loss balance — accumulated retained
   * earnings from the previous period. Negative values represent accumulated
   * losses. The closing P&L Account Reserve sent to the backend is derived as
   * `openingProfitLoss + profitAfterTax`.
   */
  openingProfitLoss: string
  /**
   * Closing P&L Account Reserve (iXBRL `ProfitLossAccountReserve`). Computed
   * from `openingProfitLoss + profitAfterTax` and kept here so existing
   * downstream code (review, drafts, backend payloads) keeps working.
   */
  profitAndLossAccount: string
}

// Step 3 (previous year): comparative figures for the prior accounting period.
// Persisted via PATCH /section/previous — the backend only stores a summary
// subset of the full P&L and Balance Sheet for comparative iXBRL reporting,
// so the user enters these as direct inputs (no detail-to-summary derivation).
export interface CT600PreviousYearForm {
  financialData: {
    turnover: string
    otherIncome: string
    costOfSales: string
    distributionCosts: string
    administrativeExpenses: string
    interestReceivable: string
    interestPayable: string
    profitBeforeTax: string
    ukTaxOnProfits: string
  }
  balanceSheet: {
    // Detail inputs — entered by the user, mirror current-year structure so
    // the previous-year experience matches.
    intangibleAssets: string
    tangibleAssets: string
    stocks: string
    debtors: string
    cashAtBankAndInHand: string
    creditorsDueAfterOneYear: string
    openingProfitLoss: string
    // Inputs that already existed (still entered directly).
    calledUpShareCapital: string
    creditorsDueWithinOneYear: string
    // Derived / summary fields kept in state so the backend save path keeps
    // working unchanged. They are auto-synced from the detail inputs above.
    netCurrentAssets: string
    totalAssetsLessCurrentLiabilities: string
    netAssets: string
    shareholdersFunds: string
    profitAndLossAccount: string
  }
}

// Step 4: Tax Computation (maps to PATCH taxComputation)
export interface CT600TaxCompForm {
  profitLossPerAccounts: string
  depreciation: string
  entertainmentDisallowable: string
  /**
   * Free-text "any other" add-back captured on the Tax Adjustments step.
   * Adds to trading profit alongside depreciation / entertainment. Maps to
   * `taxComputation.computation.otherAddBacks` on the backend.
   */
  otherAddBacks: string
  capitalAllowances: string
  bankInterest: string
  /**
   * Unused trading losses brought forward from previous accounting periods.
   * Deducted from total profits BEFORE charitable donations. Maps to
   * `taxComputation.computation.lossesBroughtForward` on the backend.
   */
  lossesBroughtForward: string
  charitableDonations: string
  /**
   * Free-text "any other deductions" catch-all. Subtracted from trading
   * profit alongside capital allowances. Maps to
   * `taxComputation.computation.otherDeductions` on the backend.
   */
  otherDeductions: string
}

// Step 5: Supplementary pages state
export interface CT600SupplementaryState {
  requirements: {
    ct600a: boolean
    ct600c: boolean
    ct600l: boolean
  }
  ct600a: {
    outstandingLoans: Array<{
      participatorName: string
      // Required by HMRC on CT600A, and by the supplementary-page model.
      participatorAddress: string
      loanAmount: string
      dateOfLoan: string
    }>
    totalOutstandingLoans: string
    taxChargeable: string
  }
  ct600c: {
    groupCompanyName: string
    groupCompanyUtr: string
    reliefAmount: string
  }
  ct600l: {
    rdExpenditure: string
    rdEnhancement: string
    rdCredit: string
  }
}

// Step 7: Submission
export interface CT600SubmissionForm {
  submittedBy: string
  submitterRole: 'director' | 'agent'
  declarationAccepted: boolean
  agentReference: string
  governmentGatewayUserId: string
  governmentGatewayPassword: string
  amendedReturn: boolean
}

// Draft persistence
export interface CT600Draft {
  draftKey: string
  companyId: string
  companyNumber: string
  companyName: string
  formId: string | null
  currentStep: CT600WizardStepId
  planSlug: string
  savedAt: string

  setup: CT600SetupForm
  companyInfo: CT600CompanyInfoForm
  financialData: CT600FinancialDataForm
  balanceSheet: CT600BalanceSheetForm
  /** Optional for legacy drafts saved before previous-year support was added. */
  previousYear?: CT600PreviousYearForm
  taxComputation: CT600TaxCompForm
  supplementary: CT600SupplementaryState
  submission: CT600SubmissionForm
}

// Default values
export const defaultSetup: CT600SetupForm = {
  companyId: '',
  companyNumber: '',
  companyName: '',
  accountingPeriodStart: '',
  accountingPeriodEnd: '',
  utr: '',
  returnType: 'full',
  planSlug: '',
}

export const defaultCompanyInfo: CT600CompanyInfoForm = {
  details: {
    returnType: 'full',
    firstReturn: false,
    finalReturn: false,
    amendedReturn: false,
    companyName: '',
    companyRegistrationNumber: '',
    utr: '',
    tradingName: '',
    accountingPeriod: { start: '', end: '' },
  },
  flags: {
    isDormant: false,
    isCloseCompany: false,
    isInvestmentCompany: false,
  },
  contact: { name: '', phone: '', email: '' },
  registeredOffice: {
    addressLine1: '',
    addressLine2: '',
    town: '',
    county: '',
    postCode: '',
    country: 'United Kingdom',
  },
  ixbrlFiling: { averageEmployees: '' },
}

export const defaultFinancialData: CT600FinancialDataForm = {
  turnover: '',
  otherIncome: '',
  costOfSales: '',
  distributionCosts: '',
  administrativeExpenses: '',
  interestReceivable: '',
  interestPayable: '',
  taxExpense: '',
}

export const defaultBalanceSheet: CT600BalanceSheetForm = {
  intangibleAssets: '',
  tangibleAssets: '',
  stocks: '',
  debtors: '',
  cashAtBankAndInHand: '',
  creditorsDueWithinOneYear: '',
  creditorsDueAfterOneYear: '',
  calledUpShareCapital: '',
  openingProfitLoss: '',
  profitAndLossAccount: '',
}

export const defaultPreviousYear: CT600PreviousYearForm = {
  financialData: {
    turnover: '',
    otherIncome: '',
    costOfSales: '',
    distributionCosts: '',
    administrativeExpenses: '',
    interestReceivable: '',
    interestPayable: '',
    profitBeforeTax: '',
    ukTaxOnProfits: '',
  },
  balanceSheet: {
    intangibleAssets: '',
    tangibleAssets: '',
    stocks: '',
    debtors: '',
    cashAtBankAndInHand: '',
    creditorsDueAfterOneYear: '',
    openingProfitLoss: '',
    calledUpShareCapital: '',
    creditorsDueWithinOneYear: '',
    netCurrentAssets: '',
    totalAssetsLessCurrentLiabilities: '',
    netAssets: '',
    shareholdersFunds: '',
    profitAndLossAccount: '',
  },
}

export const defaultTaxComp: CT600TaxCompForm = {
  profitLossPerAccounts: '',
  depreciation: '',
  entertainmentDisallowable: '',
  otherAddBacks: '',
  capitalAllowances: '',
  bankInterest: '',
  lossesBroughtForward: '',
  charitableDonations: '',
  otherDeductions: '',
}

export const defaultSupplementary: CT600SupplementaryState = {
  requirements: { ct600a: false, ct600c: false, ct600l: false },
  ct600a: {
    outstandingLoans: [],
    totalOutstandingLoans: '',
    taxChargeable: '',
  },
  ct600c: { groupCompanyName: '', groupCompanyUtr: '', reliefAmount: '' },
  ct600l: { rdExpenditure: '', rdEnhancement: '', rdCredit: '' },
}

export const defaultSubmission: CT600SubmissionForm = {
  submittedBy: '',
  submitterRole: 'director',
  declarationAccepted: false,
  agentReference: '',
  governmentGatewayUserId: '',
  governmentGatewayPassword: '',
  amendedReturn: false,
}

export const ct600Steps: CT600WizardStep[] = [
  { number: 1, title: 'Company & Period Setup' },
  { number: 2, title: 'Company Information' },
  { number: 3, title: 'Financial Statements' },
  { number: 4, title: 'Tax Adjustments' },
  { number: 5, title: 'Supplementary Pages' },
  { number: 6, title: 'Tax Computation' },
  { number: 7, title: 'Declaration & Submit' },
]
